import { useCallback, useEffect, useRef, useState } from 'react';

import { IntentMode } from '../../../../common/utils/intent-detection';
import { useDebouncedCallback } from '../../../../common/utils/use-debounced-callback';
import { useIntentDetectionVitalStats } from '../../../../controllers/quick-find/use-intent-detection-vitalstats';

import { type IntentDetectionServiceResult } from './use-intent-detection-service';

export type UseIntentRoutingArgs = {
	/**
	 * Current query text used for intent detection.
	 */
	query: string;
	/**
	 * Debounce delay for intent detection in milliseconds.
	 */
	debounceMs: number;
	/**
	 * Async intent detection function for the query.
	 */
	intentDetection: (query: string) => Promise<IntentDetectionServiceResult>;
	/**
	 * Minimum query length required to run intent detection.
	 */
	minQueryLength: number;
	/**
	 * When true, intent detection and swapping are disabled and the primary mode is pinned.
	 */
	disabled?: boolean;
	/**
	 * Used when disabled is true.
	 */
	initialPrimaryMode?: IntentMode;
};

export type UseIntentRoutingResult = {
	/**
	 * Current primary intent mode used to order the intent rows.
	 */
	primaryMode: IntentMode;
	/**
	 * The pattern type returned by the most recent intent detection response.
	 */
	patternType?: string;
	/**
	 * Whether intent detection is paused (e.g. during keyboard navigation).
	 */
	isPaused: boolean;
	/**
	 * Pause intent detection.
	 */
	pause: () => void;
	/**
	 * Resume intent detection.
	 */
	resume: () => void;
	/** Pause intent detection, then resume after timeout. */
	pauseAndResume: (timeoutMs?: number) => void;
};

/**
 * Debounced intent routing with pause/resume.
 */
export const useIntentRouting = ({
	query,
	debounceMs,
	intentDetection,
	minQueryLength,
	disabled = false,
	initialPrimaryMode = IntentMode.GNIN,
}: UseIntentRoutingArgs): UseIntentRoutingResult => {
	const [primaryMode, setPrimaryMode] = useState<IntentMode>(initialPrimaryMode);
	const [patternType, setPatternType] = useState<string | undefined>(undefined);
	const [isPaused, setIsPaused] = useState(false);
	const resumeTimeoutRef = useRef<number | null>(null);

	// Track the latest query and the query at pause time.
	const latestQueryRef = useRef(query);
	const pausedQueryRef = useRef<string | null>(null);
	// Track the last query we triggered detection for to avoid duplicate calls.
	const lastDetectedQueryRef = useRef<string | null>(null);

	// VitalStats tracking for intent detection UX task lifecycle
	const { startTask, completeTaskSuccess, abortTask, isTaskActive } =
		useIntentDetectionVitalStats();

	// Keep latestQueryRef up to date.
	latestQueryRef.current = query;

	// Pin primary mode whenever we are disabled.
	useEffect(() => {
		if (disabled) {
			setPrimaryMode(initialPrimaryMode);
		}
	}, [disabled, initialPrimaryMode]);

	const shouldRun = !disabled && query.length >= minQueryLength && !isPaused;

	const runDetection = useCallback(async () => {
		if (disabled) {
			return;
		}
		const q = latestQueryRef.current;
		if (q.length < minQueryLength) {
			return;
		}

		// VitalStats: Abort previous task if still active (superseded by new query)
		if (isTaskActive()) {
			abortTask('superseded_by_new_query');
		}

		// VitalStats: Start new UX task
		startTask(q);

		// No error handling here as the intent detection service will handle it.
		const result = await intentDetection(q);
		setPrimaryMode(result.mode);
		setPatternType(result.patternType);

		// VitalStats: Mark UX task as successfully completed (UI updated)
		completeTaskSuccess();
	}, [
		disabled,
		intentDetection,
		minQueryLength,
		isTaskActive,
		abortTask,
		startTask,
		completeTaskSuccess,
	]);

	const debouncedDetect = useDebouncedCallback(runDetection, debounceMs);

	// Trigger debounced detection when query changes (and conditions are met).
	useEffect(() => {
		if (!shouldRun) {
			return;
		}
		// Skip if we already triggered detection for this query (e.g. on resume).
		if (lastDetectedQueryRef.current === query) {
			return;
		}
		lastDetectedQueryRef.current = query;
		debouncedDetect();
		return () => {
			debouncedDetect.cancel();
		};
	}, [debouncedDetect, shouldRun, query]);

	const pause = useCallback(() => {
		pausedQueryRef.current = latestQueryRef.current;
		setIsPaused(true);
		debouncedDetect.cancel();
	}, [debouncedDetect]);

	const doResume = useCallback(() => {
		const queryChanged =
			pausedQueryRef.current !== null && pausedQueryRef.current !== latestQueryRef.current;
		pausedQueryRef.current = null;
		setIsPaused(false);
		if (queryChanged) {
			// Mark this query as detected so the effect doesn't double-trigger.
			lastDetectedQueryRef.current = latestQueryRef.current;
			// Trigger detection for the latest query that changed while paused.
			debouncedDetect();
		}
	}, [debouncedDetect]);

	const resume = useCallback(() => {
		doResume();
	}, [doResume]);

	// Pause intent detection while navigating, then resume after a short idle.
	const pauseAndResume = useCallback(
		(timeoutMs = 500) => {
			pause();
			if (resumeTimeoutRef.current) {
				window.clearTimeout(resumeTimeoutRef.current);
			}
			// Resume after a short idle window (user stops navigating).
			resumeTimeoutRef.current = window.setTimeout(() => {
				doResume();
			}, timeoutMs);
		},
		[doResume, pause],
	);

	useEffect(() => {
		return () => {
			if (resumeTimeoutRef.current) {
				window.clearTimeout(resumeTimeoutRef.current);
			}
			// VitalStats: Abort task if dialog is closed while intent detection is in-flight
			if (isTaskActive()) {
				abortTask('dialog_closed');
			}
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return { primaryMode, patternType, isPaused, pause, resume, pauseAndResume };
};
